/* eslint-disable */
import { API_URL } from '../../config';
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { isPaused, pausedUntilLabel } from '../../utils/loginPause';
import { usersAPI, adminAPI } from '../../services/api';
import { formatDate } from '../../utils/helpers';
import Companies from '../companies/Companies';
import PasswordRules from '../common/PasswordRules';
import { validatePassword, PASSWORD_POLICY_MESSAGE } from '../../utils/passwordPolicy';

const PAGE_SIZE = 50;

const today = () => new Date().toISOString().substring(0, 10);

export default function Admin() {
  const { isAdmin, isOwner, user } = useAuth();
  const isSuperAdmin = !!user?.isSuperAdmin; // SEC-004: a flag on the account, not the username

  // Users
  const [users, setUsers]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [message, setMessage]       = useState('');
  const [activeTab, setActiveTab]   = useState(isSuperAdmin ? 'platform' : 'users');
  const [showInviteModal, setShowInviteModal] = useState(false);
  // BUG FIX: used to have a separate free-text `username` field alongside
  // email — same dual-field issue found and fixed in Login.js's
  // registration form. Email is now the only identifier collected; it's
  // sent as both username and email to the backend, which also enforces
  // server-side (on this exact /api/admin/create-user endpoint) that they
  // match — so removing it here isn't just cosmetic, a mismatched
  // submission would be rejected anyway.
  const [inviteForm, setInviteForm] = useState({ password: '', role: 'member', email: '' });
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [createdCredentials, setCreatedCredentials] = useState(null); // { username, password } — shown once so admin can copy and share separately

  // SEC-004: force-delete asks for the password or an MFA code first
  const [forceDelete, setForceDelete] = useState(null); // { orgId, orgName } while the confirmation box is open
  const [fdPassword, setFdPassword]   = useState('');
  const [fdCode, setFdCode]           = useState('');
  const [fdBusy, setFdBusy]           = useState(false);
  const [fdError, setFdError]         = useState('');

  // Audit logs — paginated + filtered
  const [auditLogs, setAuditLogs]       = useState([]);
  const [auditTotal, setAuditTotal]     = useState(0);
  const [auditPage, setAuditPage]       = useState(0);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditSearch, setAuditSearch]   = useState('');
  const [auditDateFrom, setAuditDateFrom] = useState(today());
  const [auditDateTo, setAuditDateTo]     = useState(today());

  // Sessions
  const [sessions, setSessions]               = useState([]);
  const [sessionPage, setSessionPage]         = useState(0);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [activeSessionsOnly, setActiveSessionsOnly] = useState(false);
  const [sessionDateFrom, setSessionDateFrom] = useState(today());
  const [sessionDateTo, setSessionDateTo]     = useState(today());

  // Metrics
  const [metrics, setMetrics] = useState(null);

  // Platform Admin — true super admin only, cross-org view
  const [platformOrgs, setPlatformOrgs]     = useState([]);
  const [platformUsers, setPlatformUsers]   = useState([]);
  const [platformLoading, setPlatformLoading] = useState(false);
  const [platformSearch, setPlatformSearch] = useState('');
  const [platformStatusFilter, setPlatformStatusFilter] = useState('all');
  const [platformOrgFilter, setPlatformOrgFilter]       = useState('all');

  // ─── Initial load ─────────────────────────────────────────────────────────

  useEffect(() => {
    loadUsers();
    if (isSuperAdmin) {
      loadMetrics();
      loadAuditLogs(0, auditDateFrom, auditDateTo);
      loadSessions(auditDateFrom, auditDateTo);
      loadPlatformOverview();
    }
  }, [isSuperAdmin]);

  const loadPlatformOverview = async () => {
    setPlatformLoading(true);
    try {
      const BASE  = API_URL;
      const token = localStorage.getItem('token');
      const res  = await fetch(`${BASE}/api/admin/platform-overview`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setPlatformOrgs(Array.isArray(data?.organizations) ? data.organizations : []);
      setPlatformUsers(Array.isArray(data?.users) ? data.users : []);
    } catch (err) { console.error('Platform overview load error:', err); }
    finally { setPlatformLoading(false); }
  };

  // ─── Loaders ──────────────────────────────────────────────────────────────

  const loadUsers = async () => {
    setLoading(true);
    try { const u = await usersAPI.getAll(); setUsers(Array.isArray(u) ? u : []); }
    catch (err) { console.error('Error loading users:', err); setUsers([]); }
    finally { setLoading(false); }
  };

  const loadMetrics = async () => {
    try { const m = await adminAPI.getMetrics(); setMetrics(m); }
    catch {}
  };

  const loadAuditLogs = useCallback(async (page = 0, dateFrom = auditDateFrom, dateTo = auditDateTo) => {
    setAuditLoading(true);
    try {
      const BASE  = API_URL;
      const token = localStorage.getItem('token');
      // Pass plain YYYY-MM-DD strings — backend uses timezone-aware DATE() casting
      const params = new URLSearchParams({
        limit:  String(PAGE_SIZE),
        offset: String(page * PAGE_SIZE),
        ...(dateFrom && { startDate: dateFrom }),
        ...(dateTo   && { endDate:   dateTo }),
      });
      const res  = await fetch(`${BASE}/api/admin/audit-logs?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      const logs  = Array.isArray(data) ? data : (data?.logs || []);
      const total = data?.total ?? logs.length;
      setAuditLogs(logs);
      setAuditTotal(total);
      setAuditPage(page);
    } catch (err) { console.error('Audit log load error:', err); }
    finally { setAuditLoading(false); }
  }, [auditDateFrom, auditDateTo]);

  const loadSessions = useCallback(async (dateFrom = sessionDateFrom, dateTo = sessionDateTo) => {
    if (!isSuperAdmin) return;
    setSessionsLoading(true);
    try {
      const BASE  = API_URL;
      const token = localStorage.getItem('token');
      // Pass plain YYYY-MM-DD strings — backend uses timezone-aware DATE() casting
      const params = new URLSearchParams({
        ...(activeSessionsOnly && { active: 'true' }),
        ...(dateFrom && { startDate: dateFrom }),
        ...(dateTo   && { endDate:   dateTo }),
      });
      const res  = await fetch(`${BASE}/api/admin/sessions?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setSessions(Array.isArray(data) ? data : (data?.sessions || []));
      setSessionPage(0);
    } catch (err) { console.error('Sessions load error:', err); }
    finally { setSessionsLoading(false); }
  }, [isSuperAdmin, activeSessionsOnly, sessionDateFrom, sessionDateTo]);

  const logAction = async (eventAction, eventDetails = {}) => {
    try {
      await fetch(`${API_URL}/api/admin/log-action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ eventType: 'action', eventAction, eventDetails })
      });
    } catch {}
  };

  const flash = (msg) => { setMessage(msg); setTimeout(() => setMessage(''), 3000); };

  // ─── User actions ─────────────────────────────────────────────────────────

  const handleApprove = async (userId) => {
    try { await usersAPI.approve(userId); setUsers(prev => prev.map(u => u.id === userId ? { ...u, status: 'approved' } : u)); flash('User approved successfully'); }
    catch (err) { alert(err.error || 'Failed to approve user'); }
  };

  const handleReject = async (userId) => {
    if (!window.confirm('Reject this user?')) return;
    try { await usersAPI.reject(userId); setUsers(prev => prev.map(u => u.id === userId ? { ...u, status: 'rejected' } : u)); flash('User rejected'); }
    catch (err) { alert(err.error || 'Failed to reject user'); }
  };

  const handleRevoke = async (userId) => {
    if (!window.confirm('Revoke access for this user?')) return;
    try { await usersAPI.revoke(userId); setUsers(prev => prev.map(u => u.id === userId ? { ...u, status: 'revoked' } : u)); flash('User access revoked'); }
    catch (err) { alert(err.error || 'Failed to revoke user'); }
  };

  const handleUnlock = async (userId) => {
    try { await usersAPI.unlock(userId); setUsers(prev => prev.map(u => u.id === userId ? { ...u, paused_until: null } : u)); flash('Sign-in unlocked'); }
    catch (err) { alert(err.error || 'Failed to unlock user'); }
  };

  // Platform-wide actions — true super admin only, cross-org, use dedicated
  // /api/admin/platform-users endpoints (not scoped to the caller's own org)
  const platformCall = async (path, method = 'PUT') => {
    const BASE  = API_URL;
    const token = localStorage.getItem('token');
    const res = await fetch(`${BASE}${path}`, { method, headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Request failed');
    return data;
  };

  const handlePlatformApprove = async (userId) => {
    try {
      await platformCall(`/api/admin/platform-users/${userId}/approve`);
      setPlatformUsers(prev => prev.map(u => u.id === userId ? { ...u, status: 'approved' } : u));
      flash('User approved');
    } catch (err) { alert(err.message || 'Failed to approve user'); }
  };

  const handlePlatformReject = async (userId) => {
    if (!window.confirm('Reject this user?')) return;
    try {
      await platformCall(`/api/admin/platform-users/${userId}/reject`);
      setPlatformUsers(prev => prev.map(u => u.id === userId ? { ...u, status: 'rejected' } : u));
      flash('User rejected');
    } catch (err) { alert(err.message || 'Failed to reject user'); }
  };

  const handlePlatformUnlock = async (userId) => {
    try {
      await platformCall(`/api/admin/platform-users/${userId}/unlock`);
      setPlatformUsers(prev => prev.map(u => u.id === userId ? { ...u, paused_until: null } : u));
      flash('User unlocked');
    } catch (err) { alert(err.message || 'Failed to unlock user'); }
  };

  // SUPERADMIN-003: this table previously offered nothing at all for an
  // active user — Approve/Reject only appeared for pending sign-ups and
  // Unlock for locked accounts, so removing anyone meant going into the
  // database. Revoke is the everyday tool (reversible, blocks them
  // instantly even if they're logged in right now, keeps every record
  // attributed to them); Delete is permanent and only goes through when
  // they have no records that would be orphaned.
  const handlePlatformRevoke = async (u) => {
    if (!window.confirm(`Revoke ${u.username}${u.organization_name ? ` (${u.organization_name})` : ''}? They'll be blocked immediately — including if they're logged in right now. You can restore them later.`)) return;
    try {
      const data = await platformCall(`/api/admin/platform-users/${u.id}/revoke`);
      setPlatformUsers(prev => prev.map(x => x.id === u.id ? { ...x, status: 'rejected' } : x));
      flash(data.message || 'User revoked');
    } catch (err) { alert(err.message || 'Failed to revoke user'); }
  };

  const handlePlatformRestore = async (u) => {
    if (!window.confirm(`Restore ${u.username}? They'll be able to log in again immediately.`)) return;
    try {
      const data = await platformCall(`/api/admin/platform-users/${u.id}/restore`);
      setPlatformUsers(prev => prev.map(x => x.id === u.id ? { ...x, status: 'approved' } : x));
      flash(data.message || 'User restored');
    } catch (err) { alert(err.message || 'Failed to restore user'); }
  };

  const handlePlatformDelete = async (u) => {
    if (!window.confirm(`Permanently delete ${u.username}${u.organization_name ? ` (${u.organization_name})` : ''}? This cannot be undone.\n\nThis only works for a user with no records attributed to them — if they have any, nothing will be changed and you'll be told to use Revoke instead.`)) return;
    try {
      const data = await platformCall(`/api/admin/platform-users/${u.id}`, 'DELETE');
      // Update BOTH lists, not just the users one — the organisations table
      // above shows a user count per org, and leaving it stale is exactly
      // the mistake that made a deleted org's users appear to still exist.
      setPlatformUsers(prev => prev.filter(x => x.id !== u.id));
      setPlatformOrgs(prev => prev.map(o => o.id === u.organization_id ? { ...o, user_count: Math.max(0, parseInt(o.user_count, 10) - 1) } : o));
      flash(data.message || 'User deleted');
    } catch (err) { alert(err.message || 'Failed to delete user'); }
  };

  const handleSetInternal = async (orgId, currentValue) => {
    const action = currentValue ? 'remove internal (unlimited) access from' : 'grant internal (unlimited) access to';
    if (!window.confirm(`Are you sure you want to ${action} this organisation?`)) return;
    try {
      const BASE  = API_URL;
      const token = localStorage.getItem('token');
      const res = await fetch(`${BASE}/api/admin/set-internal-org`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ organization_id: orgId, is_internal: !currentValue })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed');
      setPlatformOrgs(prev => prev.map(o => o.id === orgId ? { ...o, is_internal: !currentValue } : o));
      flash(data.message || 'Updated');
    } catch (err) { alert(err.message || 'Failed to update organisation'); }
  };

  // BUG FIX: previously no way from the UI to suspend/unsuspend or delete
  // an organisation — had to be done directly in the database. The backend
  // for delete already existed (force-delete); suspend/unsuspend and this
  // UI are new.
  const handleSuspendOrg = async (orgId, orgName) => {
    if (!window.confirm(`Suspend "${orgName}"? Every user in this organisation will be blocked from logging in or using the app at all until you unsuspend it.`)) return;
    try {
      const BASE  = API_URL;
      const token = localStorage.getItem('token');
      const res = await fetch(`${BASE}/api/superadmin/organizations/${orgId}/suspend`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed');
      setPlatformOrgs(prev => prev.map(o => o.id === orgId ? { ...o, suspended_at: new Date().toISOString() } : o));
      flash(data.message || 'Suspended');
    } catch (err) { alert(err.message || 'Failed to suspend organisation'); }
  };

  const handleUnsuspendOrg = async (orgId, orgName) => {
    if (!window.confirm(`Unsuspend "${orgName}"? Access will be restored immediately.`)) return;
    try {
      const BASE  = API_URL;
      const token = localStorage.getItem('token');
      const res = await fetch(`${BASE}/api/superadmin/organizations/${orgId}/unsuspend`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed');
      setPlatformOrgs(prev => prev.map(o => o.id === orgId ? { ...o, suspended_at: null } : o));
      flash(data.message || 'Unsuspended');
    } catch (err) { alert(err.message || 'Failed to unsuspend organisation'); }
  };

  // BUG FIX: this was wired to force-delete — immediate, permanent, no
  // recovery window — as the ONLY delete option. Sam pointed out that's
  // genuinely risky as a default action: if an org needs shutting down
  // urgently (e.g. a compromised account) but the situation turns out
  // different than assumed, there was no way back once this ran. Renamed
  // to make clear what it actually does, and demoted to a deliberate,
  // secondary option — handleRequestDeleteOrg below (the same safer,
  // 30-day grace-period flow a real org owner already gets when deleting
  // their own org) is now the primary action.
  const handleForceDeleteOrg = (orgId, orgName) => {
    if (!window.confirm(`Permanently delete "${orgName}" RIGHT NOW? This destroys every property, tenant, payment and user record immediately, with no 30-day recovery window. Use this only when you're genuinely certain — for a safer option with a recovery window, use "Delete" instead.`)) return;
    if (!window.confirm(`Really sure? Type nothing, just confirm again: force-delete "${orgName}" immediately?`)) return;
    // SEC-004: the last step asks for your password or an MFA code (see the modal at the bottom of this page)
    setForceDelete({ orgId, orgName });
    setFdPassword(''); setFdCode(''); setFdError('');
  };

  const submitForceDelete = async (e) => {
    e.preventDefault();
    if (!forceDelete) return;
    setFdBusy(true); setFdError('');
    try {
      const BASE  = API_URL;
      const token = localStorage.getItem('token');
      const res = await fetch(`${BASE}/api/superadmin/organizations/${forceDelete.orgId}/force-delete`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: fdPassword || undefined, mfaCode: fdCode || undefined }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed');
      const { orgId } = forceDelete;
      setPlatformOrgs(prev => prev.filter(o => o.id !== orgId));
      setPlatformUsers(prev => prev.filter(u => u.organization_id !== orgId));
      setForceDelete(null);
      flash(data.message || 'Deleted');
    } catch (err) { setFdError(err.message || 'Failed to delete organisation'); }
    setFdBusy(false);
  };

  // The new primary action — same safer flow a real org owner gets when
  // deleting their own org: the org goes read-only immediately (so a
  // compromised account can't do further damage right away), but nothing
  // is actually destroyed for 30 days, during which handleCancelDeleteOrg
  // below can reverse it entirely if the decision turns out to be wrong.
  const handleRequestDeleteOrg = async (orgId, orgName) => {
    if (!window.confirm(`Delete "${orgName}"? This organisation will become read-only immediately and permanently deleted in 30 days. You can cancel this at any time before then.`)) return;
    try {
      const BASE  = API_URL;
      const token = localStorage.getItem('token');
      const res = await fetch(`${BASE}/api/superadmin/organizations/${orgId}/request-deletion`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed');
      setPlatformOrgs(prev => prev.map(o => o.id === orgId ? { ...o, deletion_requested_at: data.deletionRequestedAt || new Date().toISOString() } : o));
      flash(data.message || 'Deletion requested');
    } catch (err) { alert(err.message || 'Failed to request deletion'); }
  };

  const handleCancelDeleteOrg = async (orgId, orgName) => {
    if (!window.confirm(`Cancel the pending deletion of "${orgName}"? It will become fully active again immediately.`)) return;
    try {
      const BASE  = API_URL;
      const token = localStorage.getItem('token');
      const res = await fetch(`${BASE}/api/superadmin/organizations/${orgId}/cancel-deletion`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed');
      setPlatformOrgs(prev => prev.map(o => o.id === orgId ? { ...o, deletion_requested_at: null } : o));
      flash(data.message || 'Deletion cancelled');
    } catch (err) { alert(err.message || 'Failed to cancel deletion'); }
  };

  const handleDeleteUser = async (userId, username) => {
    if (!window.confirm(`Permanently delete user "${username}"? This cannot be undone.`)) return;
    const BASE = API_URL;
    try {
      const res = await fetch(`${BASE}/api/users/${userId}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }).then(r => r.json());
      if (res.error) throw new Error(res.error);
      flash(`✅ User "${username}" deleted`);
      loadUsers();
    } catch (e) { alert('Delete failed: ' + e.message); }
  };

  const handleInvite = async (e) => {
    e.preventDefault(); setInviteLoading(true); setInviteError('');
    try {
      const BASE = API_URL;
      const res = await fetch(`${BASE}/api/admin/create-user`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: inviteForm.email, password: inviteForm.password, role: inviteForm.role, email: inviteForm.email })
      }).then(r => r.json());
      if (res.error) throw res;
      // Keep the modal open, showing the credentials once so the admin can
      // copy them and send via their own channel — casavello never emails them.
      setCreatedCredentials({ username: inviteForm.email, password: inviteForm.password });
      setInviteForm({ password: '', role: 'member', email: '' });
      loadUsers();
    } catch (err) { setInviteError(err.error || 'Failed to create user'); } // UX-005: the server's message, shown in the form
    finally { setInviteLoading(false); }
  };

  const getStatusBadge = (status) => {
    const map = { approved: 'badge-success', pending: 'badge-warning', rejected: 'badge-danger', revoked: 'badge-secondary' };
    return map[status] || 'badge-secondary';
  };

  const pendingUsers = users.filter(u => u.status === 'pending');

  // Platform tab derived data
  const platformPendingCount = platformUsers.filter(u => u.status === 'pending').length;
  const platformFilteredUsers = platformUsers
    .filter(u => platformOrgFilter === 'all' || String(u.organization_id) === String(platformOrgFilter))
    .filter(u => platformStatusFilter === 'all' || u.status === platformStatusFilter)
    .filter(u => !platformSearch || u.username?.toLowerCase().includes(platformSearch.toLowerCase()) || u.email?.toLowerCase().includes(platformSearch.toLowerCase()) || u.organization_name?.toLowerCase().includes(platformSearch.toLowerCase()));

  // ─── Derived: filtered audit logs (client-side text search on current page) ─
  const filteredLogs = auditSearch
    ? auditLogs.filter(log =>
        log.username?.toLowerCase().includes(auditSearch.toLowerCase()) ||
        log.event_action?.toLowerCase().includes(auditSearch.toLowerCase()) ||
        JSON.stringify(log.event_details || '').toLowerCase().includes(auditSearch.toLowerCase())
      )
    : auditLogs;

  // Sessions paged client-side (sessions are already date-filtered server-side)
  const pagedSessions = sessions.slice(sessionPage * PAGE_SIZE, (sessionPage + 1) * PAGE_SIZE);
  const sessionTotalPages = Math.ceil(sessions.length / PAGE_SIZE);

  if (!isAdmin()) {
    return <div className="card"><div className="empty-state"><span>🔒</span><h3>Access Denied</h3><p>Admin access required</p></div></div>;
  }

  // ─── Shared date filter bar ───────────────────────────────────────────────

  const DateFilterBar = ({ dateFrom, setDateFromFn, dateTo, setDateToFn, onApply, loading: busy }) => (
    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '1rem', background: 'var(--bg-light)', padding: '0.75rem 1rem', borderRadius: '0.5rem', border: '1px solid var(--border-light)' }}>
      <div>
        <label style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '0.25rem' }}>From</label>
        <input type="date" value={dateFrom} onChange={e => setDateFromFn(e.target.value)} style={{ padding: '0.375rem 0.5rem', border: '1px solid var(--border-light)', borderRadius: '0.375rem', fontSize: '0.875rem' }} />
      </div>
      <div>
        <label style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '0.25rem' }}>To</label>
        <input type="date" value={dateTo} onChange={e => setDateToFn(e.target.value)} style={{ padding: '0.375rem 0.5rem', border: '1px solid var(--border-light)', borderRadius: '0.375rem', fontSize: '0.875rem' }} />
      </div>
      <button className="btn btn-primary" style={{ padding: '0.375rem 0.875rem', fontSize: '0.875rem' }} onClick={() => onApply()} disabled={busy}>
        {busy ? 'Loading…' : '🔍 Apply'}
      </button>
      <button className="btn btn-secondary" style={{ padding: '0.375rem 0.875rem', fontSize: '0.875rem' }} onClick={() => { const t = today(); setDateFromFn(t); setDateToFn(t); setTimeout(() => onApply(), 0); }}>
        Today
      </button>
    </div>
  );

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div>
      {/* SEC-004: a platform admin without MFA can log in, but the platform tools refuse until MFA is on */}
      {isSuperAdmin && user?.mfaSetupRequired && (
        <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '0.5rem', padding: '1rem', marginBottom: '1rem', color: '#991b1b' }}>
          <strong>⚠️ Two-step verification (MFA) is required for platform administrator accounts.</strong> Set it up in Settings, then log out and log in again with your code. The platform tools stay unavailable until then.
        </div>
      )}
      <div className="page-header">
        <h1 className="page-title">Administration</h1>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          {isOwner() && <button className="btn btn-primary" onClick={() => setShowInviteModal(true)}>+ Invite User</button>}
          <button className="btn btn-secondary" onClick={() => { loadUsers(); if (isSuperAdmin) { loadMetrics(); loadAuditLogs(0, auditDateFrom, auditDateTo); loadSessions(sessionDateFrom, sessionDateTo); } }}>🔄 Refresh</button>
        </div>
      </div>

      {message && <div className="alert alert-success">{message}</div>}

      {pendingUsers.length > 0 && (
        <div className="alert alert-warning" style={{ marginBottom: '1rem' }}>
          ⚠️ {pendingUsers.length} user(s) pending approval
        </div>
      )}

      {metrics && isSuperAdmin && (
        <div className="stats-grid" style={{ marginBottom: '1.5rem' }}>
          <div className="stat-card"><div className="stat-label">Total Users</div><div className="stat-value">{metrics.users?.total || users.length}</div></div>
          <div className="stat-card"><div className="stat-label">Active Users</div><div className="stat-value positive">{metrics.users?.approved || users.filter(u => u.status === 'approved').length}</div></div>
          <div className="stat-card"><div className="stat-label">Pending Approval</div><div className={`stat-value ${pendingUsers.length > 0 ? 'negative' : ''}`}>{pendingUsers.length}</div></div>
          <div className="stat-card"><div className="stat-label">Active Sessions</div><div className="stat-value">{metrics.activeSessions ?? '-'}</div></div>
          <div className="stat-card"><div className="stat-label">Logins Today</div><div className="stat-value">{metrics.loginsToday ?? '-'}</div></div>
          <div className="stat-card"><div className="stat-label">Failed Logins Today</div><div className={`stat-value ${(metrics.failedAttemptsToday || 0) > 0 ? 'negative' : ''}`}>{metrics.failedAttemptsToday ?? '-'}</div></div>
        </div>
      )}

      <div className="tabs" style={{ marginBottom: '1.5rem' }}>
        {[...(isSuperAdmin ? [['platform', '🌐 Platform']] : []), ['users', '👥 Users'], ['companies', '🏢 Companies'], ...(isSuperAdmin ? [['audit', '📋 Audit Logs'], ['sessions', '🔐 Sessions']] : [])].map(([val, label]) => (
          <button key={val} className={`tab-btn ${activeTab === val ? 'active' : ''}`} onClick={() => setActiveTab(val)}>
            {label}
            {val === 'platform' && platformPendingCount > 0 && (
              <span style={{ marginLeft: '0.4rem', background: 'var(--danger)', color: '#fff', borderRadius: '0.75rem', padding: '0.05rem 0.4rem', fontSize: '0.7rem', fontWeight: 700 }}>{platformPendingCount}</span>
            )}
          </button>
        ))}
      </div>

      {/* ── PLATFORM TAB — true super admin only, cross-org view ── */}
      {activeTab === 'platform' && isSuperAdmin && (
        <div>
          <div className="stats-grid" style={{ marginBottom: '1.5rem' }}>
            <div className="stat-card"><div className="stat-label">Organisations</div><div className="stat-value">{platformOrgs.length}</div></div>
            <div className="stat-card"><div className="stat-label">Total Users</div><div className="stat-value">{platformUsers.length}</div></div>
            <div className="stat-card"><div className="stat-label">Pending Approval</div><div className={`stat-value ${platformPendingCount > 0 ? 'negative' : ''}`}>{platformPendingCount}</div></div>
            <div className="stat-card"><div className="stat-label">Internal Orgs</div><div className="stat-value">{platformOrgs.filter(o => o.is_internal).length}</div></div>
          </div>

          {/* Organisations */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <h3 style={{ margin: 0, fontSize: '1.05rem' }}>Organisations</h3>
            <button className="btn btn-secondary" style={{ fontSize: '0.8rem' }} onClick={loadPlatformOverview} disabled={platformLoading}>
              {platformLoading ? 'Refreshing…' : '🔄 Refresh'}
            </button>
          </div>
          <div className="card" style={{ marginBottom: '2rem' }}>
            {platformLoading ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}>Loading organisations…</div>
            ) : platformOrgs.length === 0 ? (
              <div className="empty-state"><span>🏢</span><h3>No Organisations Found</h3></div>
            ) : (
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr><th>Organisation</th><th>Plan</th><th>Status</th><th>Users</th><th>Pending</th><th>Properties</th><th>Created</th><th>Internal</th><th>Actions</th></tr>
                  </thead>
                  <tbody>
                    {platformOrgs.map(o => (
                      <tr key={o.id}>
                        <td><strong>{o.name}</strong> <span style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>#{o.id}</span></td>
                        <td><span className="badge badge-info" style={{ textTransform: 'capitalize' }}>{o.plan_name || 'free'}</span></td>
                        <td>
                          {o.deletion_requested_at ? (
                            <span className="badge badge-danger" title={`Requested ${formatDate(o.deletion_requested_at)}`}>
                              ⏳ Deleting in {Math.max(0, 30 - Math.ceil((new Date() - new Date(o.deletion_requested_at)) / (24*60*60*1000)))}d
                            </span>
                          ) : o.suspended_at ? (
                            <span className="badge badge-danger">🚫 Suspended</span>
                          ) : (
                            <span className={`badge ${o.plan_status === 'active' ? 'badge-success' : o.plan_status === 'past_due' ? 'badge-danger' : 'badge-secondary'}`}>{o.plan_status || 'active'}</span>
                          )}
                        </td>
                        <td>{o.user_count}</td>
                        <td>{parseInt(o.pending_count) > 0 ? <span className="badge badge-warning">{o.pending_count}</span> : '—'}</td>
                        <td>{o.property_count}</td>
                        <td style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{o.created_at ? formatDate(o.created_at) : '—'}</td>
                        <td>
                          <button
                            className="btn btn-secondary"
                            style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem', background: o.is_internal ? '#dcfce7' : undefined, color: o.is_internal ? '#166534' : undefined }}
                            onClick={() => handleSetInternal(o.id, o.is_internal)}
                          >
                            {o.is_internal ? '✅ Internal' : 'Grant Internal'}
                          </button>
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                            {o.deletion_requested_at ? (
                              // Already on the safer, reversible path — the
                              // only meaningful action left is reversing it.
                              // Suspend/Delete don't apply on top of this.
                              <button className="btn btn-secondary" style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem', background: '#dcfce7', color: '#166534' }} onClick={() => handleCancelDeleteOrg(o.id, o.name)}>
                                ↩️ Cancel Deletion
                              </button>
                            ) : (
                              <>
                                {o.suspended_at ? (
                                  <button className="btn btn-secondary" style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem', background: '#dcfce7', color: '#166534' }} onClick={() => handleUnsuspendOrg(o.id, o.name)}>
                                    ✅ Unsuspend
                                  </button>
                                ) : (
                                  <button className="btn btn-secondary" style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem' }} onClick={() => handleSuspendOrg(o.id, o.name)}>
                                    🚫 Suspend
                                  </button>
                                )}
                                <button className="btn btn-danger" style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem' }} onClick={() => handleRequestDeleteOrg(o.id, o.name)}>
                                  🗑️ Delete
                                </button>
                                <button className="btn btn-secondary" style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem', color: 'var(--danger)' }} onClick={() => handleForceDeleteOrg(o.id, o.name)} title="Skips the 30-day recovery window — permanent immediately">
                                  ⚡ Force Delete
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Platform-wide users */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.75rem' }}>
            <h3 style={{ margin: 0, fontSize: '1.05rem' }}>All Users ({platformFilteredUsers.length})</h3>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <input
                placeholder="Search username, email, org…"
                value={platformSearch}
                onChange={e => setPlatformSearch(e.target.value)}
                style={{ padding: '0.375rem 0.6rem', border: '1px solid var(--border-light)', borderRadius: '0.375rem', fontSize: '0.85rem', minWidth: '220px' }}
              />
              <select value={platformStatusFilter} onChange={e => setPlatformStatusFilter(e.target.value)} style={{ padding: '0.375rem 0.6rem', border: '1px solid var(--border-light)', borderRadius: '0.375rem', fontSize: '0.85rem' }}>
                <option value="all">All statuses</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
              <select value={platformOrgFilter} onChange={e => setPlatformOrgFilter(e.target.value)} style={{ padding: '0.375rem 0.6rem', border: '1px solid var(--border-light)', borderRadius: '0.375rem', fontSize: '0.85rem' }}>
                <option value="all">All organisations</option>
                {platformOrgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </div>
          </div>
          <div className="card">
            {platformLoading ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}>Loading users…</div>
            ) : platformFilteredUsers.length === 0 ? (
              <div className="empty-state"><span>👥</span><h3>No Users Match</h3><p>Try adjusting your filters</p></div>
            ) : (
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr><th>Username</th><th>Email</th><th>Organisation</th><th>Role</th><th>Status</th><th>MFA</th><th>Created</th><th>Actions</th></tr>
                  </thead>
                  <tbody>
                    {platformFilteredUsers.map(u => (
                      <tr key={u.id}>
                        <td><strong>{u.username}</strong></td>
                        <td style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>{u.email || <span style={{ color: 'var(--border)' }}>—</span>}</td>
                        <td style={{ fontSize: '0.85rem' }}>{u.organization_name || '—'}</td>
                        <td><span className={`badge ${u.role === 'owner' ? 'badge-success' : u.role === 'admin' ? 'badge-warning' : 'badge-secondary'}`}>{u.role || 'member'}</span></td>
                        <td><span className={`badge ${getStatusBadge(u.status)}`}>{u.status}</span>{isPaused(u) && <span className="badge badge-danger" style={{ marginLeft: '0.4rem' }} title="Too many wrong passwords. Sign-in is paused and unlocks by itself, or use Unlock.">⏸ Sign-in paused until {pausedUntilLabel(u)}</span>}</td>
                        <td>{u.mfa_enabled ? <span title="MFA enabled">🔐</span> : <span style={{ color: 'var(--border)' }}>—</span>}</td>
                        <td style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{u.created_at ? formatDate(u.created_at) : '—'}</td>
                        <td>
                          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                            {u.status === 'pending' && (
                              <>
                                <button className="btn btn-success" style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem' }} onClick={() => handlePlatformApprove(u.id)}>✅ Approve</button>
                                <button className="btn btn-danger" style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem' }} onClick={() => handlePlatformReject(u.id)}>❌ Reject</button>
                              </>
                            )}
                            {isPaused(u) && (
                              <button className="btn btn-secondary" style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem' }} title="Ends the sign-in pause on every device and address" onClick={() => handlePlatformUnlock(u.id)}>🔓 Unlock</button>
                            )}
                            {/* SUPERADMIN-003. Hidden for the platform admin account and your
                                own row — the backend refuses both anyway, this just avoids
                                offering a button that can only ever fail. Pending users keep
                                their existing Approve/Reject; everyone else can be revoked
                                (or restored, if already revoked) or permanently deleted. */}
                            {!u.is_superadmin && u.id !== user?.id && u.status !== 'pending' && (
                              <>
                                {u.status === 'rejected' ? (
                                  <button className="btn btn-secondary" style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem', background: '#dcfce7', color: '#166534' }} onClick={() => handlePlatformRestore(u)}>↩️ Restore</button>
                                ) : (
                                  <button className="btn btn-secondary" style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem' }} onClick={() => handlePlatformRevoke(u)}>🚫 Revoke</button>
                                )}
                                <button className="btn btn-danger" style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem' }} onClick={() => handlePlatformDelete(u)}>🗑️ Delete</button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── USERS TAB ── */}
      {activeTab === 'users' && (
        <div className="card">
          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>Loading users...</div>
          ) : users.length === 0 ? (
            <div className="empty-state"><span>👥</span><h3>No Users Found</h3></div>
          ) : (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr><th>Username</th><th>Email</th><th>Organisation</th><th>Role</th><th>Status</th><th>Created</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id}>
                      <td><strong>{u.username}</strong></td>
                      <td style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>{u.email || <span style={{ color: 'var(--border)' }}>—</span>}</td>
                      <td>{u.organization_name || '-'}</td>
                      <td><span className={`badge ${u.role === 'owner' ? 'badge-success' : u.role === 'admin' ? 'badge-warning' : 'badge-secondary'}`}>{u.role || 'member'}</span></td>
                      <td><span className={`badge ${getStatusBadge(u.status)}`}>{u.status}</span>{isPaused(u) && <span className="badge badge-danger" style={{ marginLeft: '0.4rem' }} title="Too many wrong passwords. Sign-in is paused and unlocks by itself, or use Unlock.">⏸ Sign-in paused until {pausedUntilLabel(u)}</span>}</td>
                      <td>{formatDate(u.created_at)}</td>
                      <td>
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                          <button className="btn btn-secondary" style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }} onClick={async () => {
                            const newPw = window.prompt(`Reset password for ${u.username}.\n${PASSWORD_POLICY_MESSAGE}`);
                            if (newPw === null) return;
                            const pwProblem = validatePassword(newPw);
                            if (pwProblem) { alert(pwProblem); return; }
                            try {
                              const res = await fetch(`${API_URL}/api/admin/reset-password/${u.id}`, {
                                method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                                body: JSON.stringify({ newPassword: newPw })
                              }).then(r => r.json());
                              if (res.error) throw new Error(res.error);
                              alert(`✅ Password reset for ${u.username}`);
                            } catch (e) { alert('Failed: ' + e.message); }
                          }}>🔑 Reset PW</button>
                          {u.status === 'pending' && (
                            <>
                              <button className="btn btn-success" style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }} onClick={() => handleApprove(u.id)}>✅ Approve</button>
                              <button className="btn btn-danger" style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }} onClick={() => handleReject(u.id)}>❌ Reject</button>
                            </>
                          )}
                          {u.status === 'approved' && (
                            <button className="btn btn-secondary" style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }} onClick={() => handleRevoke(u.id)}>🔒 Revoke</button>
                          )}
                          {isPaused(u) && (
                            <button className="btn btn-secondary" style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }} title="Ends the sign-in pause on every device and address" onClick={() => handleUnlock(u.id)}>🔓 Unlock</button>
                          )}
                          {u.username !== (JSON.parse(localStorage.getItem('user') || '{}').username) && (
                            <button className="btn btn-danger" style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }} onClick={() => handleDeleteUser(u.id, u.username)}>🗑️ Delete</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── COMPANIES TAB ── */}
      {activeTab === 'companies' && (
        <Companies />
      )}

      {/* ── AUDIT LOGS TAB ── */}
      {activeTab === 'audit' && isSuperAdmin && (
        <div>
          <DateFilterBar
            dateFrom={auditDateFrom} setDateFromFn={setAuditDateFrom}
            dateTo={auditDateTo}     setDateToFn={setAuditDateTo}
            onApply={() => loadAuditLogs(0, auditDateFrom, auditDateTo)}
            loading={auditLoading}
          />

          <div style={{ marginBottom: '0.75rem' }}>
            <input
              type="text"
              placeholder="Search by user, action, or details…"
              value={auditSearch}
              onChange={e => setAuditSearch(e.target.value)}
              style={{ width: '100%', padding: '0.5rem 1rem', borderRadius: '0.5rem', border: '1px solid var(--border-light)' }}
            />
          </div>

          <div className="card">
            {auditLoading ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}>Loading audit logs…</div>
            ) : filteredLogs.length === 0 ? (
              <div className="empty-state"><span>📋</span><h3>No Audit Logs</h3><p>Try adjusting the date range</p></div>
            ) : (
              <>
                <div className="table-container">
                  <table className="table">
                    <thead><tr><th>Time</th><th>User</th><th>Action</th><th>Details</th></tr></thead>
                    <tbody>
                      {filteredLogs.map(log => (
                        <tr key={log.id}>
                          <td style={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>{new Date(log.created_at).toLocaleString('en-GB')}</td>
                          <td>{log.username}</td>
                          <td>
                            <span className={`badge ${
                              log.event_action?.includes('success') || log.event_action?.includes('created') || log.event_action?.includes('approved') ? 'badge-success' :
                              log.event_action?.includes('fail') || log.event_action?.includes('error') || log.event_action?.includes('locked') || log.event_action?.includes('rejected') ? 'badge-danger' :
                              log.event_action?.includes('logout') || log.event_action?.includes('deleted') || log.event_action?.includes('revoked') ? 'badge-secondary' :
                              log.event_action?.includes('updated') || log.event_action?.includes('changed') ? 'badge-warning' :
                              'badge-primary'
                            }`}>{log.event_action}</span>
                          </td>
                          <td style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', maxWidth: '300px', wordBreak: 'break-word' }}>
                            {typeof log.event_details === 'object' ? JSON.stringify(log.event_details) : log.event_details}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 0 0', borderTop: '1px solid var(--border-light)', marginTop: '0.5rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                  <span>Showing {auditPage * PAGE_SIZE + 1}–{Math.min((auditPage + 1) * PAGE_SIZE, auditTotal)} of {auditTotal} entries</span>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button className="btn btn-secondary" style={{ padding: '0.25rem 0.75rem', fontSize: '0.8rem' }} disabled={auditPage === 0} onClick={() => loadAuditLogs(auditPage - 1)}>← Prev</button>
                    <span style={{ padding: '0.25rem 0.5rem' }}>Page {auditPage + 1} of {Math.max(1, Math.ceil(auditTotal / PAGE_SIZE))}</span>
                    <button className="btn btn-secondary" style={{ padding: '0.25rem 0.75rem', fontSize: '0.8rem' }} disabled={(auditPage + 1) * PAGE_SIZE >= auditTotal} onClick={() => loadAuditLogs(auditPage + 1)}>Next →</button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── SESSIONS TAB ── */}
      {activeTab === 'sessions' && isSuperAdmin && (
        <div>
          <DateFilterBar
            dateFrom={sessionDateFrom} setDateFromFn={setSessionDateFrom}
            dateTo={sessionDateTo}     setDateToFn={setSessionDateTo}
            onApply={() => loadSessions(sessionDateFrom, sessionDateTo)}
            loading={sessionsLoading}
          />

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>{sessions.length} session{sessions.length !== 1 ? 's' : ''} in range</span>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.875rem' }}>
              <input type="checkbox" checked={activeSessionsOnly} onChange={e => { setActiveSessionsOnly(e.target.checked); loadSessions(sessionDateFrom, sessionDateTo); }} style={{ width: '16px', height: '16px' }} />
              Show active only
            </label>
          </div>

          <div className="card">
            {sessionsLoading ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}>Loading sessions…</div>
            ) : sessions.length === 0 ? (
              <div className="empty-state"><span>🔐</span><h3>No Sessions Found</h3><p>Try adjusting the date range</p></div>
            ) : (
              <>
                <div className="table-container">
                  <table className="table">
                    <thead><tr><th>User</th><th>Login Time</th><th>Last Activity</th><th>IP Address</th><th>Duration</th><th>Status</th></tr></thead>
                    <tbody>
                      {pagedSessions.map(session => {
                        const isActive   = !session.logout_time;
                        const lastAct    = new Date(session.last_activity || session.login_time);
                        const hoursSince = (Date.now() - lastAct) / (1000 * 60 * 60);
                        const isStale    = isActive && hoursSince > 24;
                        const duration   = session.duration_seconds ? `${Math.round(session.duration_seconds / 60)} min` : isActive ? 'Active' : '—';
                        return (
                          <tr key={session.id} style={{ opacity: isStale ? 0.6 : 1 }}>
                            <td>{session.username}</td>
                            <td style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{new Date(session.login_time).toLocaleString('en-GB')}</td>
                            <td style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{new Date(session.last_activity || session.login_time).toLocaleString('en-GB')}</td>
                            <td style={{ fontSize: '0.75rem' }}>{session.ip_address}</td>
                            <td style={{ fontSize: '0.8rem' }}>{duration}</td>
                            <td>
                              <span className={`badge ${session.logout_time ? 'badge-secondary' : isStale ? 'badge-warning' : 'badge-success'}`}>
                                {session.logout_time ? 'Logged Out' : isStale ? '⚠️ Stale' : '✅ Active'}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {sessionTotalPages > 1 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 0 0', borderTop: '1px solid var(--border-light)', marginTop: '0.5rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                    <span>Showing {sessionPage * PAGE_SIZE + 1}–{Math.min((sessionPage + 1) * PAGE_SIZE, sessions.length)} of {sessions.length}</span>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button className="btn btn-secondary" style={{ padding: '0.25rem 0.75rem', fontSize: '0.8rem' }} disabled={sessionPage === 0} onClick={() => setSessionPage(p => p - 1)}>← Prev</button>
                      <span style={{ padding: '0.25rem 0.5rem' }}>Page {sessionPage + 1} of {sessionTotalPages}</span>
                      <button className="btn btn-secondary" style={{ padding: '0.25rem 0.75rem', fontSize: '0.8rem' }} disabled={sessionPage >= sessionTotalPages - 1} onClick={() => setSessionPage(p => p + 1)}>Next →</button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* ── ADD TEAM MEMBER MODAL ── */}
      {showInviteModal && (
        <div className="modal-overlay" onClick={() => { setShowInviteModal(false); setCreatedCredentials(null); }}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '480px' }}>
            <div className="modal-header">
              <span className="modal-title">➕ Add Team Member</span>
              <button className="modal-close" onClick={() => { setShowInviteModal(false); setCreatedCredentials(null); }}>×</button>
            </div>

            {createdCredentials ? (
              /* Post-creation — show credentials once so they can be copied and shared separately */
              <div>
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '0.5rem', padding: '1rem', marginBottom: '1rem' }}>
                  <div style={{ fontWeight: 700, color: '#15803d', marginBottom: '0.5rem' }}>✅ Account created</div>
                  <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', margin: '0 0 0.75rem' }}>
                    casavello will <strong>not</strong> email these credentials. Copy them now and send to the person yourself (phone, text, in person) — this is the only time the password is shown.
                  </p>
                  <div style={{ background: '#fff', border: '1px solid #bbf7d0', borderRadius: '0.375rem', padding: '0.75rem', fontFamily: 'monospace', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                    <div>Username: <strong>{createdCredentials.username}</strong></div>
                    <div>Password: <strong>{createdCredentials.password}</strong></div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    style={{ fontSize: '0.8rem' }}
                    onClick={() => { navigator.clipboard.writeText(`Username: ${createdCredentials.username}\nPassword: ${createdCredentials.password}`); flash('Copied to clipboard'); }}
                  >
                    📋 Copy Both
                  </button>
                </div>
                <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '0.5rem', padding: '0.75rem', marginBottom: '1rem', fontSize: '0.8rem', color: '#1e40af' }}>
                  ℹ️ They'll be required to set their own password the first time they log in, and must verify their email within 48 hours. If they miss that window, the account locks and you'll need to reset it.
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button type="button" className="btn btn-primary" onClick={() => { setShowInviteModal(false); setCreatedCredentials(null); }}>Done</button>
                </div>
              </div>
            ) : (
              /* Creation form */
              <form onSubmit={handleInvite} style={{ padding: '0 0.25rem' }}>
                {inviteError && <div className="alert alert-danger" style={{ marginBottom: '1rem' }}>{inviteError}</div>}
                <div className="form-group"><label>Temporary Password *</label><input type="password" required value={inviteForm.password || ''} onChange={e => setInviteForm(p => ({ ...p, password: e.target.value }))} placeholder="Choose a strong password" autoComplete="new-password" /><PasswordRules password={inviteForm.password} /></div>
                <div className="form-group">
                  <label>Role</label>
                  <select value={inviteForm.role} onChange={e => setInviteForm(p => ({ ...p, role: e.target.value }))}>
                    <option value="member">Member</option>
                    <option value="owner">Owner (Admin)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Email Address *</label>
                  <input type="email" required value={inviteForm.email || ''} onChange={e => setInviteForm(p => ({ ...p, email: e.target.value }))} placeholder="user@example.com" autoComplete="off" />
                  <small style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>This is also their login — they'll be sent a verification link (no password) — required within 48 hours.</small>
                </div>
                <div style={{ background: '#fef3c7', border: '1px solid #fde047', borderRadius: '0.5rem', padding: '0.75rem', marginBottom: '1rem', fontSize: '0.8rem', color: '#92400e' }}>
                  {/* BUG FIX: "or the account locks" was stale — VERIFY-003
                      replaced the 48-hour hard lockout with a write-only
                      restriction months ago (reads always remain available).
                      Also fixed a leftover reference to the old brand name. */}
                  ⚠️ <strong>casavello will not email this password to anyone.</strong> Once created, you'll need to share it with them yourself. They must log in, set their own password and verify their email within 48 hours, or saving/editing anything is paused until they do.
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                  <button type="button" className="btn btn-secondary" onClick={() => { setShowInviteModal(false); setCreatedCredentials(null); }}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={inviteLoading || validatePassword(inviteForm.password) !== null}>{inviteLoading ? 'Creating...' : '➕ Create Account'}</button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* ── SEC-004: CONFIRM FORCE-DELETE WITH PASSWORD OR MFA CODE ── */}
      {forceDelete && (
        <div className="modal-overlay" onClick={() => setForceDelete(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '440px' }}>
            <div className="modal-header">
              <span className="modal-title">🔒 Confirm permanent deletion</span>
              <button className="modal-close" onClick={() => setForceDelete(null)}>×</button>
            </div>
            <form onSubmit={submitForceDelete}>
              <p style={{ fontSize: '0.9rem' }}>To permanently delete <strong>{forceDelete.orgName}</strong> immediately, enter your password <em>or</em> a current code from your authenticator app.</p>
              <div className="form-group"><label>Your password</label>
                <input type="password" autoComplete="current-password" value={fdPassword} onChange={e => setFdPassword(e.target.value)} />
              </div>
              <div className="form-group"><label>or MFA code</label>
                <input type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={8} value={fdCode} onChange={e => setFdCode(e.target.value)} />
              </div>
              {fdError && <div style={{ color: 'var(--danger)', marginBottom: '0.75rem' }}>{fdError}</div>}
              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setForceDelete(null)}>Cancel</button>
                <button type="submit" className="btn btn-primary" style={{ background: 'var(--danger)' }} disabled={fdBusy || (!fdPassword && !fdCode)}>{fdBusy ? 'Deleting...' : 'Delete permanently'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
